import { AHSeparator, AHText, Box, ListItem } from "arkh";
import { graphql, useFragment } from "react-relay";
import { FlatList } from "react-native";

import React, { useCallback, useMemo, useState } from "react";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";

import { OrgSearchInput } from "../../OrgSearchInput";

import { distinct } from "@/src/utils";
import { OrgTeamListItem } from "./OrgTeamListItem";
import { OrgAdminTeamsSelector_teams$key } from "./__generated__/OrgAdminTeamsSelector_teams.graphql";
import { WorkspaceEmptyFooterItem } from "../../WorkspaceEmptyListItem";

export function OrgAdminTeamSelector(props: {
  teams: OrgAdminTeamsSelector_teams$key;
  selectedTeams: string[];
  setSelectedTeams: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const [search, setSearch] = useState("");
  const { selectedTeams, setSelectedTeams } = props;
  const teams = useFragment(
    graphql`
      fragment OrgAdminTeamsSelector_teams on Team @relay(plural: true) {
        name
        id
        ...OrgTeamListItem_team
      }
    `,
    props.teams
  );

  const allTeams = useMemo(() => {
    return (
      teams
        .slice()
        .sort((a, b) =>
          a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1
        ) ?? []
    );
  }, [teams]);
  const listData = useMemo(() => {
    if (search.length > 0) {
      const normalizedSearch = search.toLocaleLowerCase();
      return allTeams.filter((s) =>
        s.name.toLocaleLowerCase().includes(normalizedSearch)
      );
    }
    return allTeams;
  }, [allTeams, search]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      const isSelected = selectedTeams.includes(item.id);
      return (
        <OrgTeamListItem
          isSelected={isSelected}
          onToggleSelect={() => {
            if (isSelected) {
              setSelectedTeams(selectedTeams.filter((t) => t !== item.id));
            } else {
              setSelectedTeams([...selectedTeams, item.id]);
            }
          }}
          team={item}
          isLast={index === listData.length - 1}
          onPress={() => {}}
        />
      );
    },
    [listData.length, selectedTeams, setSelectedTeams]
  );

  const intl_tokens = useIntlTokens();

  const isAllVisibleSelected = useMemo(() => {
    return listData.every((ld) => selectedTeams.includes(ld.id));
  }, [selectedTeams, listData]);

  const checkboxStatus = useMemo(() => {
    if (selectedTeams.length === 0) {
      return false;
    }

    if (allTeams.every((t) => selectedTeams.includes(t.id))) {
      return true;
    }
    return "indeterminate";
  }, [selectedTeams, allTeams]);

  function onToggleSelect() {
    if (isAllVisibleSelected) {
      setSelectedTeams((prev) =>
        prev.filter((t) => !listData.map((ld) => ld.id).includes(t))
      );
    } else {
      setSelectedTeams((prev) =>
        distinct([...prev, ...listData.map((t) => t.id)])
      );
    }
  }
  return (
    <React.Fragment>
      <FlatList
        data={listData}
        keyboardShouldPersistTaps="handled"
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={AHSeparator}
        stickyHeaderIndices={[0]}
        ListEmptyComponent={<WorkspaceEmptyFooterItem />}
        ListHeaderComponent={
          <Box color="surface-background-light">
            <OrgSearchInput
              placeholder={intl_tokens.Search}
              value={search}
              onChangeText={setSearch}
            />
            <Box color="surface-background-light">
              <ListItem
                displayMode="TABLE"
                border="border-primary"
                onPress={() => {
                  onToggleSelect();
                }}
                left={
                  <ListItem.MultiSelectIndicator selected={checkboxStatus} />
                }
              >
                <Box
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <AHText type="subtitle">{intl_tokens.Name}</AHText>
                </Box>
              </ListItem>
              <AHSeparator />
            </Box>
          </Box>
        }
      />
    </React.Fragment>
  );
}
